import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import Checklist from './components/Checklist';
import ExpenseSummary, { formatHKD } from './components/ExpenseSummary';
import NoteEditor from './components/NoteEditor';
import PushReminders from './components/PushReminders';
import { Expense, createId, initialExpenses, monthKey } from './data/expenses';

const STORAGE_KEY = 'hk-bills-checklist-v2';
const LEGACY_KEY = 'hk-bills-checklist-v1';

interface StoredData {
  /** Earliest month this app was used — navigation never goes before it */
  firstMonth: string;
  /** One expense array per month (key: yyyy-mm) */
  months: Record<string, Expense[]>;
}

function todayMonth(): string {
  return monthKey(new Date());
}

function latestMonthKey(months: Record<string, Expense[]>): string | null {
  return Object.keys(months).sort().pop() ?? null;
}

/** Fresh copy of a month's bills for the next month: new ids, all unpaid */
function rolloverCopy(items: Expense[]): Expense[] {
  return items.map((e) => ({ ...e, id: createId(), completed: false }));
}

function loadData(): StoredData {
  const today = todayMonth();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.months && typeof parsed.months === 'object') {
        const months: Record<string, Expense[]> = { ...parsed.months };
        const firstMonth =
          typeof parsed.firstMonth === 'string' && parsed.firstMonth ? parsed.firstMonth : today;
        // Roll over: opening in a month we haven't reached yet creates a fresh
        // unpaid copy of the most recent month's bills (never goes back before
        // the month the app was first opened).
        if (!months[today]) {
          const latest = latestMonthKey(months);
          months[today] = latest ? rolloverCopy(months[latest]) : [...initialExpenses];
        }
        return { firstMonth, months };
      }
    }
    // Legacy v1 storage: a flat array, owned by today's month.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const arr = JSON.parse(legacy);
      if (Array.isArray(arr) && arr.length) {
        return { firstMonth: today, months: { [today]: arr } };
      }
    }
  } catch {
    /* corrupted storage — fall through to defaults */
  }
  return { firstMonth: today, months: { [today]: [...initialExpenses] } };
}

function App() {
  const [data, setData] = useState<StoredData>(loadData);
  const [month, setMonth] = useState<string>(todayMonth());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** 'list' = checklist; 'totals' = monthly total-expense view (v0.9) */
  const [view, setView] = useState<'list' | 'totals'>('list');

  const expenses = useMemo(() => data.months[month] ?? [], [data.months, month]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* storage full or unavailable — ignore */
    }
  }, [data]);

  const monthList = useMemo(() => Object.keys(data.months).sort(), [data.months]);
  const monthIndex = monthList.indexOf(month);
  const canGoPrev = monthIndex > 0 && month > data.firstMonth;
  const canGoNext = monthIndex >= 0 && monthIndex < monthList.length - 1;

  const goPrev = () => {
    if (monthIndex > 0) setMonth(monthList[monthIndex - 1]);
  };
  const goNext = () => {
    if (monthIndex >= 0 && monthIndex < monthList.length - 1) setMonth(monthList[monthIndex + 1]);
  };

  const completedCount = useMemo(
    () => expenses.filter((e) => e.completed).length,
    [expenses]
  );
  const progress = expenses.length ? Math.round((completedCount / expenses.length) * 100) : 0;

  // Unpaid-amount summary (v0.14): how much is still outstanding this month.
  const unpaidCount = expenses.length - completedCount;
  const unpaidAmount = useMemo(
    () =>
      expenses.reduce(
        (sum, e) => sum + (!e.completed && typeof e.amount === 'number' ? e.amount : 0),
        0
      ),
    [expenses]
  );
  const hasUnpaidAmount = expenses.some((e) => !e.completed && typeof e.amount === 'number');

  const monthDate = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1);
  const monthLabel = monthDate.toLocaleDateString('en-HK', { month: 'long', year: 'numeric' });
  const todayLabel = new Date().toLocaleDateString('en-HK', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const editingItem = editingId ? expenses.find((e) => e.id === editingId) ?? null : null;

  /** Apply an update to the currently viewed month's list */
  const updateMonthExpenses = (updater: (prev: Expense[]) => Expense[]) => {
    setData((prevData) => ({
      ...prevData,
      months: { ...prevData.months, [month]: updater(prevData.months[month] ?? []) },
    }));
  };

  const handleToggle = (id: string) => {
    updateMonthExpenses((prev) =>
      prev.map((e) => (e.id === id ? { ...e, completed: !e.completed } : e))
    );
  };

  const handleSave = (item: Expense) => {
    updateMonthExpenses((prev) => {
      const exists = prev.some((e) => e.id === item.id);
      return exists ? prev.map((e) => (e.id === item.id ? item : e)) : [...prev, item];
    });
    setEditingId(null);
    setCreating(false);
  };

  const handleDelete = (id: string) => {
    updateMonthExpenses((prev) => prev.filter((e) => e.id !== id));
    setEditingId(null);
    setCreating(false);
  };

  /** Insert an exact copy right after the original, then open it for tweaking */
  const handleDuplicate = (id: string) => {
    const original = expenses.find((e) => e.id === id);
    if (!original) return;
    const copy: Expense = { ...original, id: createId(), completed: false };
    updateMonthExpenses((prev) => {
      const idx = prev.findIndex((e) => e.id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setEditingId(copy.id);
    setCreating(false);
  };

  /** Rebuild the stored array to match a new user-chosen order */
  const handleReorder = (ids: string[]) => {
    updateMonthExpenses((prev) => {
      const byId = new Map(prev.map((e) => [e.id, e]));
      const reordered = ids.map((id) => byId.get(id)).filter((e): e is Expense => !!e);
      return reordered.length === prev.length ? reordered : prev;
    });
  };

  const openEditor = (id: string) => {
    setEditingId(id);
    setCreating(false);
  };

  const openNew = () => {
    setCreating(true);
    setEditingId('__new__');
  };

  const closeEditor = () => {
    setEditingId(null);
    setCreating(false);
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-inner">
          <div className="app-title-group">
            <span className="app-kicker">
              {view === 'list' ? 'Monthly bills' : 'Total expenses'}
            </span>
            <div className="app-title-row">
              <div className="app-month-nav">
                <button
                  type="button"
                  className="month-nav"
                  onClick={goPrev}
                  disabled={!canGoPrev}
                  aria-label="Previous month"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                    <path
                      d="M15 6l-6 6 6 6"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <h1 className="app-title">{monthLabel}</h1>
                <button
                  type="button"
                  className="month-nav"
                  onClick={goNext}
                  disabled={!canGoNext}
                  aria-label="Next month"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                    <path
                      d="M9 6l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              {view === 'list' && <p className="app-today">Today · {todayLabel}</p>}
            </div>
          </div>
        </div>
        <div className="app-header-actions">
          <div className="header-actions">
            <PushReminders bills={expenses} />
            {view === 'list' ? (
              <button
                type="button"
                className="btn-icon"
                onClick={() => setView('totals')}
                aria-label="Show monthly total"
                title="Monthly total"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
                  <path
                    d="M5 20v-8M12 20V5M19 20v-5"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                className="btn-icon"
                onClick={() => setView('list')}
                aria-label="Back to checklist"
                title="Back to checklist"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
                  <path
                    d="M11 6l-6 6 6 6M5 12h14"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
          {view === 'list' && (
            <button type="button" className="btn-add" onClick={openNew} aria-label="Add new bill">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        <div className="app-progress" aria-hidden="true">
          <div className="app-progress-track">
            <div className="app-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <p className="app-progress-label">
          {completedCount} of {expenses.length} paid <span className="app-version">· v0.14</span>
        </p>
        {view === 'list' && unpaidCount > 0 && (
          <p className="app-unpaid-label">
            {unpaidCount} unpaid{hasUnpaidAmount ? ` · ${formatHKD(unpaidAmount)}` : ''}
          </p>
        )}
      </header>

      <main className="app-main">
        {view === 'totals' ? (
          <ExpenseSummary items={expenses} />
        ) : (
          <Checklist
            items={expenses}
            onToggle={handleToggle}
            onEdit={openEditor}
            onReorder={handleReorder}
          />
        )}
      </main>

      {(creating || editingItem) && (
        <NoteEditor
          key={creating ? '__new__' : editingItem!.id}
          item={editingItem}
          isNew={creating}
          onSave={handleSave}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onClose={closeEditor}
        />
      )}
    </div>
  );
}

export default App;
